import { builder } from '@builder.io/react';
import { BuilderContent } from '@builder.io/sdk';
import { QueryClient, UseQueryResult, useQuery } from '@tanstack/react-query';
import { UserAttributes, queryKeys } from '@/api/apiConfig';
import { MenuItem, mapCmsMegaMenuMenus, mapFooterMenus } from '@/api/cms/mapMenuTree';
import { BrowserDeviceType, SettingsResponse } from '@/generated/data.schemas';
import { DrawerMegaMenuItem } from '@/types/drawerMegaMenuItem';

const fetchContent = async (params: {
	modelName: string;
	locale: string;
	url?: string;
	userAttributes?: UserAttributes;
}): Promise<null | BuilderContent> => {
	const result = await builder
		.get(params.modelName, {
			url: params.url,
			userAttributes: {
				...params.userAttributes,
				locale: params.locale,
			},

			options: {
				locale: params.locale,
				enrich: true,
			},
		})
		.promise();
	return result ?? null;
};

export type CmsTaggedContentSortOrder = 'newest' | 'oldest';

const fetchTaggedCmsContent = async (params: {
	locale: string;
	modelName: string;
	tags: readonly string[];
	maxItems: number;
	sortOrder: CmsTaggedContentSortOrder;
	userAttributes?: UserAttributes;
}): Promise<BuilderContent[]> => {
	if (params.tags.length === 0 || params.modelName.length === 0) {
		return [];
	}

	try {
		const result = await builder.getAll(params.modelName, {
			limit: params.maxItems,
			query: {
				$or: [
					{ tags: { $in: params.tags } },
					{ 'data.tags': { $in: params.tags } },
				],
			},
			sort: {
				createdDate: params.sortOrder === 'newest' ? -1 : 1,
			},
			userAttributes: {
				...params.userAttributes,
				locale: params.locale,
			},
			options: {
				enrich: true,
				locale: params.locale,
			},
		});

		return result;
	} catch (error) {
		console.error(
			`Error fetching tagged content for locale ${params.locale} and model ${params.modelName}`,
			error,
		);
	}

	return [];
};

type BuilderDevice = 'mobile' | 'tablet' | 'desktop';

export const convertToBuilderDevice = (device: BrowserDeviceType | null): BuilderDevice => {
	switch (device) {
		case BrowserDeviceType.Mobile:
			return 'mobile';
		case BrowserDeviceType.Tablet:
			return 'tablet';
		case BrowserDeviceType.Desktop:
		default:
			return 'desktop';
	}
};

export const useCmsContentQuery = (params: {
	url?: string;
	settings: SettingsResponse | undefined;
	modelName: string;
	userAttributes?: UserAttributes;
}): UseQueryResult<BuilderContent | null> => {
	const cultureCode = params.settings?.cultureCode ?? '';
	const device = convertToBuilderDevice(params.settings?.device ?? null);
	const segmentation = params.settings?.segmentations.find((x) => x.isCurrentSegmentation === true)?.name;

	return useQuery({
		queryKey: queryKeys.cms.page({
			url: params.url,
			locale: cultureCode,
			modelName: params.modelName,
			userAttributes: { device, segmentation },
		}).queryKey,
		queryFn: () =>
			fetchContent({
				url: params.url,
				locale: cultureCode,
				modelName: params.modelName,
				userAttributes: { device, segmentation },
			}),
		enabled: params.settings !== undefined,
	});
};

export const useTaggedCmsContentQuery = (
	settings: SettingsResponse | undefined,
	params: {
		modelName: string;
		tags: readonly string[];
		maxItems: number;
		sortOrder: CmsTaggedContentSortOrder;
		enabled?: boolean;
	},
): UseQueryResult<BuilderContent[]> => {
	const cultureCode = settings?.cultureCode ?? '';
	const device = convertToBuilderDevice(settings?.device ?? null);
	const segmentation = settings?.segmentations.find((x) => x.isCurrentSegmentation === true)?.name;
	const isEnabled = (params.enabled ?? true) && params.modelName.length > 0 && params.tags.length > 0;

	return useQuery({
		queryKey: queryKeys.cms.taggedPages({
			locale: cultureCode,
			modelName: params.modelName,
			tags: params.tags,
			maxItems: params.maxItems,
			sortOrder: params.sortOrder,
			userAttributes: { device, segmentation },
		}).queryKey,
		queryFn: () =>
			fetchTaggedCmsContent({
				locale: cultureCode,
				modelName: params.modelName,
				tags: params.tags,
				maxItems: params.maxItems,
				sortOrder: params.sortOrder,
				userAttributes: { device, segmentation },
			}),
		enabled: !!settings && isEnabled,
	});
};

export const cmsContentServerSideFetch = (
	queryClient: QueryClient,
	url: string,
	settings: SettingsResponse | undefined,
	modelName: string,
): Promise<void> => {
	const cultureCode = settings?.cultureCode ?? '';
	const device = convertToBuilderDevice(settings?.device ?? null);
	const segmentation = settings?.segmentations.find((x) => x.isCurrentSegmentation === true)?.name;

	return queryClient.prefetchQuery({
		queryKey: queryKeys.cms.page({
			url,
			locale: cultureCode,
			modelName,
			userAttributes: { device, segmentation },
		}).queryKey,
		queryFn: async () => {
			return fetchContent({ url, locale: cultureCode, modelName, userAttributes: { device, segmentation } });
		},
	});
};

const menuItemLimit = 10;
const submenuItemCountLimit = 25;

/* Footer */
const fetchCmsMenu = async (
	locale: string,
	modelName: string,
	userAttributes?: UserAttributes,
): Promise<MenuItem[]> => {
	try {
		const options = {
			fields: 'id,data.title,data.order,data.internalLink.value.data.url,data.externalLink,data.items,data.children',
			limit: menuItemLimit,
			userAttributes: {
				...userAttributes,
				locale: locale,
			},
			options: {
				enrich: true,
				locale,
			},
		};
		for (let i = 0; i < submenuItemCountLimit; i++) {
			options.fields =
				options.fields +
				`,data.items[${i}].title,data.items[${i}].internalLink.value.data.url,data.items[${i}].externalLink`;
		}
		const result = await builder.getAll(modelName, options);

		if (result.length > 0) {
			return mapFooterMenus(result);
		}
		return [];
	} catch (error) {
		console.error(`Error fetching menu items for locale ${locale} and model ${modelName}`, error);
	}
	return [];
};

export const useCmsMenuQuery = (
	settings: SettingsResponse | undefined,
	modelName: string,
): UseQueryResult<MenuItem[]> => {
	const cultureCode = settings?.cultureCode ?? '';
	const device = convertToBuilderDevice(settings?.device ?? null);
	const segmentation = settings?.segmentations.find((x) => x.isCurrentSegmentation === true)?.name;

	return useQuery({
		queryKey: queryKeys.cms.menu(cultureCode, modelName, { device, segmentation }).queryKey,
		queryFn: () => fetchCmsMenu(cultureCode, modelName, { device, segmentation }),
		enabled: !!settings,
	});
};
//-----------------------------------------------------

/* CMS mega menu in drawer */
const fetchCmsMegaMenu = async (
	locale: string,
	modelName: string,
	userAttributes?: UserAttributes,
): Promise<DrawerMegaMenuItem[]> => {
	try {
		const options = {
			limit: menuItemLimit,
			userAttributes: {
				...userAttributes,
				locale: locale,
			},
			options: {
				enrich: true,
				locale,
			},
		};

		const result = await builder.getAll(modelName, options);

		if (result.length > 0) {
			return mapCmsMegaMenuMenus(result);
		}
		return [];
	} catch (error) {
		console.error(`Error fetching menu items for locale ${locale} and model ${modelName}`, error);
	}
	return [];
};

export const useCmsMegaMenuQuery = (
	settings: SettingsResponse | undefined,
	modelName: string,
): UseQueryResult<DrawerMegaMenuItem[]> => {
	const cultureCode = settings?.cultureCode ?? '';
	const device = convertToBuilderDevice(settings?.device ?? null);
	const segmentation = settings?.segmentations.find((x) => x.isCurrentSegmentation === true)?.name;

	return useQuery({
		queryKey: queryKeys.cms.megaMenu(cultureCode, modelName, { device, segmentation }).queryKey,
		queryFn: () => fetchCmsMegaMenu(cultureCode, modelName, { device, segmentation }),
		enabled: !!settings,
	});
};

export const cmsMegaMenuServerSideFetch = (
	queryClient: QueryClient,
	settings: SettingsResponse | undefined,
	modelName: string,
): Promise<void> => {
	const cultureCode = settings?.cultureCode ?? '';
	const device = convertToBuilderDevice(settings?.device ?? null);
	const segmentation = settings?.segmentations.find((x) => x.isCurrentSegmentation === true)?.name;

	return queryClient.prefetchQuery({
		queryKey: queryKeys.cms.megaMenu(cultureCode, modelName, { device, segmentation }).queryKey,
		queryFn: async () => {
			return fetchCmsMegaMenu(cultureCode, modelName, { device, segmentation });
		},
	});
};

//-----------------------------------------------------

/* USP menu */
const fetchCmsUSPMenu = async (
	locale: string,
	modelName: string,
	userAttributes?: UserAttributes,
): Promise<MenuItem[]> => {
	try {
		const options = {
			limit: menuItemLimit,
			userAttributes: {
				...userAttributes,
				locale: locale,
			},
			options: {
				enrich: true,
				locale,
			},
		};

		const result = await builder.getAll(modelName, options);

		if (result.length > 0) {
			return result
				.sort((x, y) => x.data?.order - y.data?.order)
				.map((item) => {
					return {
						id: item.id,
						title: item.data?.title,
						internalLink: item.data?.internalLink,
						externalLink: item.data?.externalLink,
						children: item.data?.items,
					};
				});
		}
		return [];
	} catch (error) {
		console.error(`Error fetching menu items for locale ${locale} and model ${modelName}`, error);
	}
	return [];
};

export const useCmsUSPMenuQuery = (
	settings: SettingsResponse | undefined,
	modelName: string,
	isSalesPortal?: boolean,
): UseQueryResult<MenuItem[]> => {
	const cultureCode = settings?.cultureCode ?? '';
	const device = convertToBuilderDevice(settings?.device ?? null);
	const segmentation = settings?.segmentations.find((x) => x.isCurrentSegmentation === true)?.name;

	return useQuery({
		queryKey: queryKeys.cms.menu(cultureCode, modelName, { device, segmentation }).queryKey,
		queryFn: () => fetchCmsUSPMenu(cultureCode, modelName, { device, segmentation }),
		enabled: !!settings && !isSalesPortal,
	});
};

export type HreflangUrl = {
	url: string;
};

const fetchCmsHrefLangs = async (modelName: string, hreflangGroupName: string): Promise<HreflangUrl[]> => {
	try {
		const options = {
			limit: submenuItemCountLimit,
			options: { noTargeting: true }, // fetch all pages
			query: {
				['data.hreflangGroup']: { $eq: hreflangGroupName },
			},
			fields: 'data.url',
		};

		const result = await builder.getAll(modelName, options);

		if (result.length > 0) {
			return result.map((page) => ({
				url: page.data?.url || '',
			}));
		}
		return [];
	} catch (error) {
		console.error(`Error fetching hrefLang items for hreflangGroupName ${hreflangGroupName}`, error);
	}
	return [];
};

export const useCmsHrefLangQuery = (
	settings: SettingsResponse | undefined,
	modelName: string,
	hreflangGroupName: string,
): UseQueryResult<HreflangUrl[]> => {
	const segmentation = settings?.segmentations.find((x) => x.isCurrentSegmentation === true)?.name;

	return useQuery({
		queryKey: queryKeys.cms.hrefLangs(modelName, hreflangGroupName, { segmentation }).queryKey,
		queryFn: () => fetchCmsHrefLangs(modelName, hreflangGroupName),
		enabled: !!settings && hreflangGroupName.length > 0,
	});
};
